import { BarcodeDetector, prepareZXingModule } from 'barcode-detector/ponyfill';
import wasmUrl from 'zxing-wasm/reader/zxing_reader.wasm?url';

/**
 * El `.wasm` se sirve desde nuestro propio dominio.
 *
 * Por defecto zxing-wasm se lo descarga de jsDelivr EN TIEMPO DE EJECUCIÓN, así
 * que sin conexión el escáner se quedaría colgado en silencio — inaceptable en
 * una app offline-first. De paso, una petición menos a un tercero.
 * Ver DECISIONS.md D-19.
 */
prepareZXingModule({
  overrides: {
    locateFile: (path: string, prefix: string) =>
      path.endsWith('.wasm') ? wasmUrl : prefix + path,
  },
  fireImmediately: false,
});

/** Los formatos de un supermercado europeo. QR incluido por si acaso. */
export const BARCODE_FORMATS = [
  'ean_13',
  'ean_8',
  'upc_a',
  'upc_e',
  'code_128',
  'qr_code',
] as const;

export interface Detector {
  detect(source: CanvasImageSource): Promise<{ rawValue: string }[]>;
  /** 'nativo' si lo resuelve el sistema, 'wasm' si lo hace zxing. */
  kind: 'nativo' | 'wasm';
}

interface NativeDetectorCtor {
  new (options: { formats: readonly string[] }): {
    detect(source: CanvasImageSource): Promise<{ rawValue: string }[]>;
  };
  getSupportedFormats(): Promise<readonly string[]>;
}

/**
 * Usa la API nativa donde existe y cae a WebAssembly donde no.
 *
 * El polyfill NO es opcional: `BarcodeDetector` nativo no existe en Safari, ni
 * en Firefox, ni en Chrome de Windows o Linux. Solo Chrome de Android y de
 * macOS lo traen.
 */
export async function createDetector(): Promise<Detector> {
  const Native = (globalThis as { BarcodeDetector?: NativeDetectorCtor }).BarcodeDetector;

  if (Native) {
    try {
      const supported = await Native.getSupportedFormats();
      const usable = BARCODE_FORMATS.filter((f) => supported.includes(f));
      if (usable.length > 0) {
        const detector = new Native({ formats: usable });
        return { kind: 'nativo', detect: (source) => detector.detect(source) };
      }
    } catch {
      // Si el nativo falla al consultarse, se usa el de siempre.
    }
  }

  const detector = new BarcodeDetector({ formats: [...BARCODE_FORMATS] });
  return { kind: 'wasm', detect: (source) => detector.detect(source) };
}

/**
 * Valida el dígito de control de EAN-13/EAN-8/UPC.
 *
 * Una lectura borrosa puede devolver dígitos plausibles pero incorrectos;
 * comprobar el dígito de control cuesta seis líneas y evita buscar un producto
 * que no existe.
 */
export function hasValidChecksum(code: string): boolean {
  if (!/^\d{8}$|^\d{12,13}$/.test(code)) return true; // Otros formatos: no aplica.
  if (mod10(code)) return true;

  // 8 dígitos también puede ser un UPC-E, cuyo dígito de control se calcula
  // sobre su UPC-A expandido: validarlo como EAN-8 rechazaba casi la mitad.
  const upcA = code.length === 8 ? expandUpcE(code) : null;
  return upcA != null && mod10(upcA);
}

/** Dígito de control EAN/UPC: pesos 3 y 1 alternos empezando por el final del cuerpo. */
function mod10(code: string): boolean {
  const digits = [...code].map(Number);
  const check = digits.pop()!;
  const sum = digits
    .reverse()
    .reduce((acc, digit, index) => acc + digit * (index % 2 === 0 ? 3 : 1), 0);
  return (10 - (sum % 10)) % 10 === check;
}

/**
 * UPC-E (sistema 0 o 1, 8 dígitos) → su UPC-A de 12, con las reglas de
 * expansión estándar según el último dígito de datos. null si no es UPC-E.
 */
export function expandUpcE(code: string): string | null {
  if (!/^[01]\d{7}$/.test(code)) return null;
  const d = (i: number) => code.charAt(i);
  const [n, a, b, c, dd, e, f, check] = [d(0), d(1), d(2), d(3), d(4), d(5), d(6), d(7)];

  let body: string;
  if (f === '0' || f === '1' || f === '2') body = `${a}${b}${f}0000${c}${dd}${e}`;
  else if (f === '3') body = `${a}${b}${c}00000${dd}${e}`;
  else if (f === '4') body = `${a}${b}${c}${dd}00000${e}`;
  else body = `${a}${b}${c}${dd}${e}0000${f}`;
  return `${n}${body}${check}`;
}

/**
 * El código con el que buscar el producto. Open Food Facts indexa los UPC-E
 * por su UPC-A: un UPC-E de verdad (que no es un EAN-8 válido) se expande.
 */
export function barcodeForLookup(code: string): string {
  if (code.length !== 8 || mod10(code)) return code;
  const upcA = expandUpcE(code);
  return upcA != null && mod10(upcA) ? upcA : code;
}
